/**
 * ProductImagesSubTab V2 - Orchestrator
 *
 * Sub-Tab for product images. M1-Redesign: ALL channels are stacked vertically;
 * a ChannelFilterBar restricts which channel sections are visible ("Alle Kanäle"
 * = all). Cross-container drag & drop, pending upload/copy state, inheritance and
 * the global save bar are unchanged.
 *
 * Built with Tailwind CSS v4.1 and V2 components.
 * WCAG 2.1 Level AA + BFSG konform
 */

import { useState, useMemo, useEffect, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import type { ProductAsset } from '@/types/products';
import { getProductAssets, syncProductImages } from '@/lib/api/products';
import type { QueueImage } from '@/lib/api/image-queue';
import { QueueGeneratedImages } from '@/components/v2/channel-readiness/QueueGeneratedImages';
import { ImageCardContent } from './ImageCardContent';
import { ChannelFilterBar, type ChannelFilterItem } from './ChannelFilterBar';
import { ChannelGridList } from './ChannelGridList';
import { addLibraryAssetToChannel } from './libraryAdd';
import { AssetLibraryPanel } from './AssetLibraryPanel';
import { ChannelVersionHistoryModal } from './ChannelVersionHistoryModal';
import { ChannelSettingsModal } from './ChannelSettingsModal';
import { ProductImagesHeader } from './ProductImagesHeader';
import { ProductImagesBottomBar } from './ProductImagesBottomBar';
import { LightboxOverlay } from './LightboxOverlay';
import { usePendingOperations } from './usePendingOperations';
import { useImageChanges } from './useImageChanges';
import { useChannelOrders } from './useChannelOrders';
import { useOrderedAssets } from './useOrderedAssets';
import { useImageDragDrop } from './useImageDragDrop';
import { useSaveHandler } from './useSaveHandler';
import { useImageOptimization } from './useImageOptimization';
import { useUploadHandler } from './useUploadHandler';
import { usePendingDeletes } from './usePendingDeletes';
import { DroppableAIRefSection } from './DroppableAIRefSection';
import { useAIRefDrop } from './useAIRefDrop';
import type { ImageSize } from './types';
import { IMAGE_SIZES } from './types';

export type { ImageSize } from './types';

interface ProductImagesSubTabProps {
  productId: string;
  imageSize: ImageSize;
  setImageSize: (size: ImageSize) => void;
  queueImages?: QueueImage[];
}

export function ProductImagesSubTab({ productId, imageSize, setImageSize, queueImages }: ProductImagesSubTabProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [syncing, setSyncing] = useState(false);
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState<ProductAsset | null>(null);
  const [galleryChannelId, setGalleryChannelId] = useState<string | null>(null);
  const [galleryChannelName, setGalleryChannelName] = useState<string>('');
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyChannelId, setHistoryChannelId] = useState<string | null>(null);
  const [historyChannelName, setHistoryChannelName] = useState<string>('');
  const [channelSettingsOpen, setChannelSettingsOpen] = useState(false);
  const [channelSettingsId, setChannelSettingsId] = useState<string | null>(null);
  const [channelSettingsName, setChannelSettingsName] = useState<string>('');
  const [channelSettingsTab, setChannelSettingsTab] = useState<'requirements' | 'optimization'>('requirements');
  // M1: 'all' = alle Channels sichtbar; sonst nur der gewählte Channel-Key.
  const [activeFilter, setActiveFilter] = useState<string>('all');

  // Keyboard shortcut: L to toggle library
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.key === 'l' || e.key === 'L') && !(e.target as HTMLElement)?.matches?.('input, textarea')) {
        e.preventDefault();
        setIsLibraryOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Data query
  const assetsQuery = useQuery({
    queryKey: ['product-assets', productId],
    queryFn: () => getProductAssets(productId),
    staleTime: 30_000,
  });
  const assets = assetsQuery.data?.assets || [];
  const groupedByChannel = useMemo(() => assetsQuery.data?.grouped_by_channel || {}, [assetsQuery.data?.grouped_by_channel]);
  const channelMeta = useMemo(() => assetsQuery.data?.channel_meta || {}, [assetsQuery.data?.channel_meta]);

  // Hooks
  const pending = usePendingOperations();
  const changes = useImageChanges();
  const orders = useChannelOrders(groupedByChannel, changes.setChangedAssetIds, changes.setChangedChannelKeys);
  const getOrderedAssets = useOrderedAssets(groupedByChannel, orders.channelOrders, pending.pendingCopies, pending.pendingUploads, changes.changedChannelKeys);
  const { optimizing, optimizingChannel, handleOptimizeImages } = useImageOptimization(productId, assetsQuery);
  const pendingDel = usePendingDeletes();
  const handleUploadSuccess = useUploadHandler(groupedByChannel, pending.setPendingUploads, changes.setChangedChannelKeys);

  const hasChanges = useMemo(() => {
    if (pendingDel.pendingDeleteCount > 0 || pendingDel.pendingReplacements.length > 0) return true;
    if (pending.pendingCopies.length > 0 || pending.pendingUploads.length > 0) return true;
    for (const state of orders.channelOrders.values()) {
      if (JSON.stringify(state.originalOrder) !== JSON.stringify(state.currentOrder)) return true;
    }
    return changes.changedChannelKeys.size > 0;
  }, [orders.channelOrders, changes.changedChannelKeys, pending.pendingCopies, pending.pendingUploads, pendingDel.pendingDeleteCount, pendingDel.pendingReplacements]);

  const clearAll = useCallback(() => {
    orders.setChannelOrders(new Map());
    changes.clearChanges();
    pending.clearPending();
    pendingDel.clearAllDeletes();
  }, [orders, changes, pending, pendingDel]);

  const { saving, handleSaveAll, handleCancelChanges } = useSaveHandler({
    productId, pendingDeletes: pendingDel.pendingDeletes, pendingReplacements: pendingDel.pendingReplacements,
    pendingCopies: pending.pendingCopies, pendingUploads: pending.pendingUploads,
    channelOrders: orders.channelOrders, changedChannelKeys: changes.changedChannelKeys,
    clearAll, assetsQuery, queryClient,
  });

  const dnd = useImageDragDrop({
    getOrderedAssets, groupedByChannel, pendingCopies: pending.pendingCopies,
    pendingUploads: pending.pendingUploads, handleReorder: orders.handleReorder,
    setPendingCopies: pending.setPendingCopies, setPendingUploads: pending.setPendingUploads,
    setChangedChannelKeys: changes.setChangedChannelKeys,
  });

  const aiRefDrop = useAIRefDrop(productId);

  const handleDragEnd = useCallback(
    async (event: Parameters<typeof dnd.handleDragEnd>[0]) => {
      const overData = event.over?.data.current as Record<string, unknown> | undefined;
      const overType = overData?.type as string | undefined;
      const overSlot = overData?.slot as number | undefined;
      const assetId = dnd.activeDragData?.asset?.asset_id;

      // Drop onto a deleted slot → replacement (not reorder)
      if (overData?.isDeletedSlot && assetId && overSlot !== undefined) {
        const chId = overData.channelId as string;
        const oldLinkId = overData.oldLinkId as string;
        const sourceAsset = dnd.activeDragData?.asset;
        const sourceChannelKey = dnd.activeDragData?.channelKey;
        if (chId && oldLinkId && sourceAsset) {
          pendingDel.addReplacement(chId, oldLinkId, overSlot, sourceAsset.asset_id, sourceAsset.width || 0, sourceAsset.height || 0);
          // Remove source from its original position
          const isPendingUploadSource = sourceAsset.is_pending || sourceAsset.link_id?.startsWith('pending-upload-');
          if (isPendingUploadSource && sourceChannelKey) {
            // Remove pending upload from source channel
            pending.setPendingUploads(prev => prev.filter(p => !(p.assetId === sourceAsset.asset_id && p.channelKey === sourceChannelKey)));
          } else if (sourceChannelKey) {
            // Existing image from same channel: mark source for deletion too
            const srcChannelId = dnd.activeDragData?.channelId;
            const srcSlot = sourceAsset.slot ?? sourceAsset.sort_number ?? 0;
            if (srcChannelId && srcSlot > 0) {
              pendingDel.markForDeletion(srcChannelId, sourceAsset.link_id, srcSlot);
            }
          }
          dnd.handleDragEnd({ ...event, over: null });
          return;
        }
      }

      await dnd.handleDragEnd(event);
      if (overType === 'ai-ref-slot' && assetId && overSlot !== undefined) {
        aiRefDrop.handleAIRefDrop(overSlot, assetId);
      }
    },
    [dnd, aiRefDrop, pendingDel, pending],
  );

  // Sorted & filtered channel keys — show all active channels (even empty ones)
  const sortedChannelKeys = useMemo(() => {
    return Object.keys(groupedByChannel)
      .filter((key) => {
        if (key === 'global') return true;
        // Wawi-Check via channel_meta (zuverlässiger als Asset-Daten)
        const meta = channelMeta[key];
        if (meta) {
          const jtlId = meta.channel_id_jtl?.toLowerCase() || '';
          if (jtlId === '1-1-0' || jtlId.startsWith('3-') || jtlId.startsWith('4-')) return false;
        }
        // Fallback: Wawi-Check via erstem Asset
        const ch = groupedByChannel[key];
        if (ch && ch.length > 0) {
          const ct = typeof ch[0].channel_type === 'string' ? ch[0].channel_type.toLowerCase() : '';
          if (ct.includes('wawi')) return false;
        }
        return true;
      })
      .sort((a, b) => (a === 'global' ? -1 : b === 'global' ? 1 : a.localeCompare(b)));
  }, [groupedByChannel, channelMeta]);

  // Filter-Items + Counts für die ChannelFilterBar (Count = sichtbar gestapelte Bilder).
  // 'global' ist KEIN eigener Chip — der "Alle Kanäle"-Chip deckt die Gesamtsicht ab
  // (sonst doppeltes "Alle Kanäle"). Die Global-Sektion bleibt im Stapel sichtbar.
  const filterItems = useMemo<ChannelFilterItem[]>(() => {
    return sortedChannelKeys
      .filter((key) => key !== 'global')
      .map((key) => ({
        key,
        name: channelMeta[key]?.channel_name ?? key,
        count: getOrderedAssets(key).length,
      }));
  }, [sortedChannelKeys, channelMeta, getOrderedAssets]);

  // "Alle Kanäle"-Chip zeigt ALLE Bilder inkl. der Global-Sektion.
  const totalImageCount = useMemo(
    () => sortedChannelKeys.reduce((sum, key) => sum + getOrderedAssets(key).length, 0),
    [sortedChannelKeys, getOrderedAssets],
  );

  // Reset auf "Alle", falls der gewählte Filter-Channel verschwindet.
  useEffect(() => {
    if (activeFilter !== 'all' && !sortedChannelKeys.includes(activeFilter)) {
      setActiveFilter('all');
    }
  }, [activeFilter, sortedChannelKeys]);

  const visibleChannelKeys = useMemo(
    () => (activeFilter === 'all' ? sortedChannelKeys : sortedChannelKeys.filter((k) => k === activeFilter)),
    [activeFilter, sortedChannelKeys],
  );

  // Sync handler
  const handleSyncAll = async () => {
    setSyncing(true);
    try {
      const result = await syncProductImages(productId, false);
      if (result.success) {
        toast.success(t('toast.success.imagesSynced', { count: result.images_synced }));
        clearAll();
        assetsQuery.refetch();
      } else { toast.error(result.error || 'Sync fehlgeschlagen'); }
    } catch { toast.error(t('errors.syncing')); }
    finally { setSyncing(false); }
  };

  // Modal handlers
  // per-Channel „+"/„Aus Galerie wählen" → öffnet die Library kanal-kontextualisiert
  // (galleryChannelId/Name = Ziel-Channel; Bild per Drag&Drop in die Channel-Sektion).
  const handleOpenGallery = useCallback((channelId: string, channelName: string) => {
    setGalleryChannelId(channelId);
    setGalleryChannelName(channelName);
    setIsLibraryOpen(true);
  }, []);

  const handleViewSettings = (channelId?: string, channelName?: string) => {
    if (!channelId) return;
    setChannelSettingsId(channelId);
    setChannelSettingsName(channelName || '');
    setChannelSettingsTab('optimization');
    setChannelSettingsOpen(true);
  };

  const handleViewHistory = (channelId: string, channelName: string) => {
    setHistoryChannelId(channelId);
    setHistoryChannelName(channelName);
    setHistoryModalOpen(true);
  };

  const handleViewRequirements = (channelId: string, channelName: string) => {
    setChannelSettingsId(channelId);
    setChannelSettingsName(channelName);
    setChannelSettingsTab('requirements');
    setChannelSettingsOpen(true);
  };

  return (
    <>
      <DndContext
        sensors={dnd.sensors}
        collisionDetection={dnd.collisionDetection}
        onDragStart={dnd.handleDragStart}
        onDragOver={dnd.handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <div className="flex h-full">
          <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
            <ProductImagesHeader
              assets={assets}
              imageSize={imageSize}
              setImageSize={setImageSize}
              syncing={syncing}
              saving={saving}
              optimizing={optimizing}
              isLibraryOpen={isLibraryOpen}
              onSyncAll={handleSyncAll}
              onViewSettings={handleViewSettings}
              onOptimizeImages={handleOptimizeImages}
              onToggleLibrary={() => { if (!isLibraryOpen) setGalleryChannelId(null); setIsLibraryOpen(!isLibraryOpen); }}
            />
            {queueImages && queueImages.length > 0 && (
              <div className="mb-4">
                <QueueGeneratedImages images={queueImages} />
              </div>
            )}
            <DroppableAIRefSection productId={productId} savingSlot={aiRefDrop.savingSlot} />
            <div className="px-4 py-3 border-b border-neutral-200 dark:border-neutral-800">
              <ChannelFilterBar
                channels={filterItems}
                activeFilter={activeFilter}
                onChange={setActiveFilter}
                totalCount={totalImageCount}
              />
            </div>
            <ChannelGridList
              assetsQuery={assetsQuery}
              assets={assets}
              sortedChannelKeys={visibleChannelKeys}
              getOrderedAssets={getOrderedAssets}
              orders={orders}
              changes={changes}
              pending={pending}
              dnd={dnd}
              productId={productId}
              imageSize={imageSize}
              channelMeta={channelMeta}
              optimizingChannel={optimizingChannel}
              handleOpenGallery={handleOpenGallery}
              handleUploadSuccess={handleUploadSuccess}
              handleOptimizeImages={handleOptimizeImages}
              handleViewSettings={handleViewSettings}
              handleViewHistory={handleViewHistory}
              handleViewRequirements={handleViewRequirements}
              setLightboxImage={setLightboxImage}
              handleSyncAll={handleSyncAll}
              syncing={syncing}
              onDeleteSlot={pendingDel.markForDeletion}
              isPendingDelete={pendingDel.isPendingDelete}
              isCascadeDelete={pendingDel.isCascadeDelete}
              onUndoDelete={pendingDel.unmarkDeletion}
              getReplacement={pendingDel.getReplacement}
            />
            <LightboxOverlay image={lightboxImage} onClose={() => setLightboxImage(null)} />
            {historyChannelId && (
              <ChannelVersionHistoryModal isOpen={historyModalOpen} onClose={() => setHistoryModalOpen(false)} productId={productId} channelId={historyChannelId} channelName={historyChannelName} />
            )}
            {channelSettingsId && (
              <ChannelSettingsModal isOpen={channelSettingsOpen} onClose={() => setChannelSettingsOpen(false)} productId={productId} channelId={channelSettingsId} channelName={channelSettingsName} initialTab={channelSettingsTab} />
            )}
          </div>
          <DragOverlay adjustScale={false}>
            {dnd.activeDragData ? (
              <div style={{ width: IMAGE_SIZES[imageSize], maxWidth: IMAGE_SIZES[imageSize] }}>
                <ImageCardContent asset={dnd.activeDragData.asset} index={0} isOverlay />
              </div>
            ) : null}
          </DragOverlay>
          {isLibraryOpen && <AssetLibraryPanel productId={productId} onClose={() => { setIsLibraryOpen(false); setGalleryChannelId(null); }} targetChannel={galleryChannelId ? { id: galleryChannelId, name: galleryChannelName } : null} onAddAsset={(asset) => { if (galleryChannelId) addLibraryAssetToChannel(asset, galleryChannelId, { groupedByChannel, channelMeta, pendingUploads: pending.pendingUploads, pendingCopies: pending.pendingCopies, setPendingUploads: pending.setPendingUploads, setChangedChannelKeys: changes.setChangedChannelKeys, channelName: galleryChannelName }); }} />}
        </div>
      </DndContext>
      <ProductImagesBottomBar
        hasChanges={hasChanges}
        saving={saving}
        changedChannelKeys={changes.changedChannelKeys}
        changedAssetIds={changes.changedAssetIds}
        pendingDeleteCount={pendingDel.pendingDeleteCount}
        onSave={handleSaveAll}
        onCancel={handleCancelChanges}
      />
    </>
  );
}
